import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  IProjectDataService,
  Project,
  ProjectFilters,
  PaginatedResult,
  CreateProjectDto,
  ProjectOrderMove,
} from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

@Injectable()
export class ProjectDataCloudService implements IProjectDataService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);

  private get baseUrl(): string {
    return `${this.config.apiBaseUrl}/project`;
  }

  find(
    page: number,
    limit: number,
    filters?: ProjectFilters
  ): Observable<PaginatedResult<Project>> {
    let params = new HttpParams()
      .set('skip', String((page - 1) * limit))
      .set('limit', String(limit));

    if (filters?.projectName) {
      params = params.set('project_name', filters.projectName);
    }
    if (filters?.categoryId) {
      params = params.set('project_category_id', filters.categoryId);
    }
    if (filters?.manufacturingMethod) {
      params = params.set(
        'project_manufacturing_method',
        filters.manufacturingMethod
      );
    }
    if (filters?.sortBy) {
      params = params.set('sort_by', filters.sortBy);
    }
    if (filters?.sortOrder) {
      params = params.set('sort_order', filters.sortOrder);
    }

    return this.http.get<Record<string, unknown>>(this.baseUrl, { params }).pipe(
      map((response) => ({
        data: (response['projects'] ?? response['data'] ?? []) as Project[],
        total: (response['total'] ?? response['count'] ?? 0) as number,
        page,
        limit,
      }))
    );
  }

  findOne(id: string): Observable<Project> {
    return this.http.get<Project>(`${this.baseUrl}/${id}`);
  }

  findOneProjectDetails(id: string): Observable<Project> {
    return this.http.get<Project>(`${this.baseUrl}/${id}/details`);
  }

  create(data: CreateProjectDto): Observable<Project> {
    return this.http.post<Project>(this.baseUrl, data);
  }

  update(id: string, data: Partial<Project>): Observable<Project> {
    return this.http.patch<Project>(`${this.baseUrl}/${id}`, data);
  }

  updateName(id: string, name: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/rename`, { name });
  }

  updateStatus(id: string, status: string): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/${id}`, { status });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  duplicate(id: string): Observable<Project> {
    return this.http.post<Project>(`${this.baseUrl}/${id}/duplicate`, {});
  }

  setCategory(id: string, categoryId: string | null): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/category`, {
      category_id: categoryId,
    });
  }

  setPinned(id: string, pinned: boolean): Observable<void> {
    return this.http.post<{ pinned: boolean }>(`${this.baseUrl}/${id}/pin`, { pinned }).pipe(map(() => undefined));
  }

  moveInOrder(id: string, move: ProjectOrderMove): Observable<void> {
    return this.http
      .post<{ position: number | null }>(`${this.baseUrl}/${id}/position`, { place: move.place, anchor_id: move.anchorId })
      .pipe(map(() => undefined));
  }

  createSoftJawsFromResult(
    sourceProjectId: string,
    resultType: 'organic' | 'prismatic',
  ): Observable<{ projectId: string }> {
    return this.http.post<{ projectId: string }>(
      `${this.baseUrl}/${sourceProjectId}/create-softjaws`,
      { resultType },
    );
  }
}
